import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export interface RetentionPolicy {
  id: string;
  name: string;
  video_retention_days: number;
  video_delete_enabled: boolean;
  feed_retention_days: number;
  audit_retention_days: number;
  created_at?: unknown;
  updated_at?: unknown;
}

export interface StorageUsage {
  by_tier: Record<"hot" | "cold" | "glacier", { count: number; bytes: number }>;
  totals: { count: number; bytes: number };
  eligibility: {
    eligible_count: number;
    legal_hold_count: number;
    incident_flagged_count: number;
    total_over_age: number;
    cutoff_utc: string;
  };
  delete_enabled: boolean;
}

export interface RecordingTier {
  id: string;
  storage_tier: "hot" | "cold" | "glacier";
  legal_hold: boolean;
  incident_flagged: boolean;
  restore_status: "none" | "restoring" | "restored" | "failed";
  restore_requested_at?: unknown;
  restore_available_at?: unknown;
}

const POLICY_KEY = ["storage", "policy"] as const;
const USAGE_KEY = ["storage", "usage"] as const;

export function useRetentionPolicy() {
  return useQuery({
    queryKey: POLICY_KEY,
    queryFn: async () => {
      const { data } = await api.get<RetentionPolicy>(
        "/api/v1/storage/retention-policy",
      );
      return data;
    },
  });
}

export function useUpdateRetentionPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<{
      name: string;
      video_retention_days: number;
      video_delete_enabled: boolean;
      feed_retention_days: number;
      audit_retention_days: number;
    }>) => {
      const { data } = await api.put<RetentionPolicy>(
        "/api/v1/storage/retention-policy", patch,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: POLICY_KEY });
      qc.invalidateQueries({ queryKey: USAGE_KEY });
    },
  });
}

export function useStorageUsage() {
  return useQuery({
    queryKey: USAGE_KEY,
    queryFn: async () => {
      const { data } = await api.get<StorageUsage>(
        "/api/v1/storage/usage",
      );
      return data;
    },
    refetchInterval: 60_000,
  });
}

export function useSetLegalHold() {
  return useMutation({
    mutationFn: async (input: { id: string; reason?: string }) => {
      const { data } = await api.post<RecordingTier>(
        `/api/v1/recordings/${input.id}/legal-hold`,
        { reason: input.reason },
      );
      return data;
    },
  });
}

export function useClearLegalHold() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<RecordingTier>(
        `/api/v1/recordings/${id}/legal-hold`,
      );
      return data;
    },
  });
}

export function useRequestRestore() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<RecordingTier>(
        `/api/v1/recordings/${id}/restore`,
      );
      return data;
    },
  });
}
